import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  Box,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  Divider,
  Chip,
  Avatar,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { supabase } from '../supabaseClient';

const BUCKET_URL = "https://wwwikvfwquvafkoopemw.supabase.co/storage/v1/object/public/manga_versus/images/characters/";

export default function CharacterRankingsModal({ open, onClose, character }) {
  const [rankings, setRankings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all'); // 'all', 'power_level', 'popularity'
  const [characterDetails, setCharacterDetails] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(0);
  const [dragStartScrollLeft, setDragStartScrollLeft] = useState(0);
  const scrollContainerRef = useRef(null);

  // Add scroll functions
  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -300, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 300, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    if (open && character) {
      fetchCharacterRankings();
    }
  }, [open, character]);

  const fetchCharacterRankings = async () => {
    setLoading(true);
    try {
      const characterId = character.character_id || character.id;
      console.log('Fetching rankings for character ID:', characterId);
      
      // Get character details from characters table
      const { data: characterDetails, error: charDetailsError } = await supabase
        .from('characters')
        .select('image_url, name, manga, role, gender, affiliation')
        .eq('id', characterId)
        .single();

      if (charDetailsError) {
        console.error('Character details error:', charDetailsError);
      }

      // First, get this character's scores (only existing columns)
      const { data: characterScores, error: charError } = await supabase
        .from('character_tag_scores')
        .select('tag, type, score, wins, losses')
        .eq('character_id', characterId);

      if (charError) {
        console.error('Character scores error:', charError);
        throw charError;
      }

      if (!characterScores || characterScores.length === 0) {
        setRankings([]);
        return;
      }

      // Store character details for use in display
      setCharacterDetails(characterDetails);

      // For each tag, get all scores to compute rank
      const rankingsWithRank = await Promise.all(
        characterScores.map(async (charScore) => {
          try {
            // Get all characters' scores for this tag, ordered by score desc
            const { data: allScores, error: rankError } = await supabase
              .from('character_tag_scores')
              .select('score')
              .eq('tag', charScore.tag)
              .eq('type', charScore.type) // Only count scores of the same type
              .order('score', { ascending: false });

            if (rankError) throw rankError;

            // Calculate rank by finding position in sorted list
            const rank = allScores.findIndex(s => s.score <= charScore.score) + 1;
            const totalCharacters = allScores.length; // Get total count

            // Calculate winrate from wins and losses
            const totalBattles = charScore.wins + charScore.losses;
            const winrate = totalBattles > 0 ? charScore.wins / totalBattles : 0;

            return {
              tag: charScore.tag,
              type: charScore.type,
              score: charScore.score,
              wins: charScore.wins,
              losses: charScore.losses,
              winrate: winrate,
              rank: rank,
              totalCharacters: totalCharacters, // Add total to the return object
              total_battles: totalBattles
            };
          } catch (error) {
            console.error(`Error calculating rank for tag ${charScore.tag}:`, error);
            
            // Calculate winrate even for error case
            const totalBattles = charScore.wins + charScore.losses;
            const winrate = totalBattles > 0 ? charScore.wins / totalBattles : 0;
            
            return {
              tag: charScore.tag,
              type: charScore.type,
              score: charScore.score,
              wins: charScore.wins,
              losses: charScore.losses,
              winrate: winrate,
              rank: 999,
              totalCharacters: 999, // Default for error case
              total_battles: totalBattles
            };
          }
        })
      );

      console.log('Rankings with computed rank:', rankingsWithRank);
      
      // Sort by rank (best rank first)
      const sortedData = rankingsWithRank.sort((a, b) => a.rank - b.rank);
      setRankings(sortedData);
      
    } catch (error) {
      console.error('Error fetching character rankings:', error);
      setRankings([]);
    } finally {
      setLoading(false);
    }
  };

  const getTagColor = (tag) => {
    const colors = {
      'Global': '#f49330',
      'power_level': '#ff6b6b',
      'popularity': '#4ecdc4',
      'favorite': '#45b7d1',
      'Male': '#6c5ce7',
      'Female': '#fd79a8',
      'Protagonist': '#00b894',
      'Antagonist': '#e17055',
      'Anti-Hero': '#a29bfe',
      'Side Character': '#fdcb6e',
      'Naruto': '#ff9500',
      'Bleach': '#0066cc',
      'Chainsaw Sandenvinci': '#cc0000',
      'Yuu Hakusho': '#00cc66',
      'Grimjemy Jeegerjequins': '#9900cc',
      'Minato Namikaze': '#ffcc00',
      'Kabuto Yakushi': '#666666',
      'Nsohuna Giiga': '#ff6600',
    };
    return colors[tag] || '#95a5a6';
  };

  const getTagTitleColor = (type) => {
    if (type === 'power_level') return '#6366f1'; // Blue for Power
    if (type === 'popularity') return '#f49330'; // Orange for Popularity
    return '#95a5a6'; // Default gray
  };

  const formatWinrate = (winrate) => {
    return `${(winrate * 100).toFixed(1)}%`;
  };

  const getRankSuffix = (rank) => {
    if (rank % 10 === 1 && rank % 100 !== 11) return 'st';
    if (rank % 10 === 2 && rank % 100 !== 12) return 'nd';
    if (rank % 10 === 3 && rank % 100 !== 13) return 'rd';
    return 'th';
  };

  const getFilteredRankings = () => {
    if (typeFilter === 'all') return rankings;
    return rankings.filter(ranking => ranking.type === typeFilter);
  };

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragStart(e.clientX);
    setDragStartScrollLeft(scrollContainerRef.current.scrollLeft);
    e.preventDefault();
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    
    const walk = (e.clientX - dragStart); // Removed the * 2 multiplier
    scrollContainerRef.current.scrollLeft = dragStartScrollLeft - walk;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(
    `(max-width:600px), (max-height:600px)`
  );

  if (!character) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Paper
        sx={{
          background: 'rgba(35, 35, 35, 0.95)',
          backdropFilter: 'blur(15px)',
          WebkitBackdropFilter: 'blur(15px)',
          border: '2px solid #f49330',
          borderRadius: 4,
          p: { xs: 1, sm: 2, md: 4 },
          width: { xs: '92vw', sm: 600, md: 900, lg: 1200 },
          maxWidth: { xs: '92vw', sm: '98vw' },
          height: { xs: 'auto', sm: 600, md: 800, lg: 900 },
          maxHeight: { xs: '92vh', sm: '98vh' },
          overflowY: 'auto',
          color: '#fff',
          display: 'flex',
          flexDirection: 'column',
          gap: 0, // slightly reduced gap
        }}
      >
        {/* Top Section - Character Info */}
        <Box
          sx={{
            display: isSmallScreen ? 'none' : 'flex',
            gap: 4,
            flexShrink: 0,
            height: 'auto',
          }}
        >
          {/* Character Image - Left Side */}
          <Box sx={{ flexShrink: 0 }}>
            <Box
              component="img"
              src={characterDetails?.image_url || `${BUCKET_URL}${character.image_filename || character.image_url?.split('/').pop()}`}
              alt={character.name || character.characters?.name}
              sx={{
                width: 180,
                height: 320,
                objectFit: 'cover',
                borderRadius: 3,
                border: '3px solid #f49330',
                background: '#181818',
              }}
            />
          </Box>

          {/* Character Details - Right of Image */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            {/* Character Header */}
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  background: 'linear-gradient(45deg, #f49330 30%, #fff176 90%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 1,
                }}
              >
                {characterDetails?.name || character.name || character.characters?.name}
              </Typography>
              <Typography variant="h6" sx={{ color: '#bbb', mb: 2 }}>
                {characterDetails?.manga || character.manga || character.characters?.manga}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {(characterDetails?.role || character.role || character.characters?.role) && (
                  <Chip
                    label={characterDetails?.role || character.role || character.characters?.role}
                    size="small"
                    sx={{ background: '#444', color: '#fff' }}
                  />
                )}
                {(characterDetails?.gender || character.gender || character.characters?.gender) && (
                  <Chip
                    label={characterDetails?.gender || character.gender || character.characters?.gender}
                    size="small"
                    sx={{ background: '#444', color: '#fff' }}
                  />
                )}
                {(characterDetails?.affiliation || character.affiliation || character.characters?.affiliation) && 
                  (characterDetails?.affiliation || character.affiliation || character.characters?.affiliation).map((aff, index) => (
                    <Chip
                      key={index}
                      label={aff}
                      size="small"
                      sx={{ background: '#444', color: '#fff' }}
                    />
                  ))}
              </Box>
            </Box>

            {/* Rankings Title */}
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                mb: 2,
                color: '#fff',
              }}
            >
              Character Rankings
            </Typography>

            {/* Filter Buttons */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <Paper
                onClick={() => setTypeFilter('all')}
                sx={{
                  px: 3,
                  py: 1.5,
                  cursor: 'pointer',
                  background: typeFilter === 'all' ? 'rgba(244, 147, 48, 0.2)' : 'rgba(46, 46, 46, 0.8)',
                  border: typeFilter === 'all' ? '2px solid #f49330' : '2px solid #555',
                  borderRadius: 2,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: '#f49330',
                    background: 'rgba(244, 147, 48, 0.1)',
                  },
                }}
              >
                <Typography sx={{ color: '#fff', fontWeight: 600 }}>
                  All Types
                </Typography>
              </Paper>
              
              <Paper
                onClick={() => setTypeFilter('power_level')}
                sx={{
                  px: 3,
                  py: 1.5,
                  cursor: 'pointer',
                  background: typeFilter === 'power_level' ? 'rgba(99, 102, 241, 0.2)' : 'rgba(46, 46, 46, 0.8)',
                  border: typeFilter === 'power_level' ? '2px solid #6366f1' : '2px solid #555',
                  borderRadius: 2,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: '#6366f1',
                    background: 'rgba(99, 102, 241, 0.1)',
                  },
                }}
              >
                <Typography sx={{ color: '#fff', fontWeight: 600 }}>
                  Power
                </Typography>
              </Paper>
              
              <Paper
                onClick={() => setTypeFilter('popularity')}
                sx={{
                  px: 3,
                  py: 1.5,
                  cursor: 'pointer',
                  background: typeFilter === 'popularity' ? 'rgba(244, 147, 48, 0.2)' : 'rgba(46, 46, 46, 0.8)',
                  border: typeFilter === 'popularity' ? '2px solid #f49330' : '2px solid #555',
                  borderRadius: 2,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: '#f49330',
                    background: 'rgba(244, 147, 48, 0.1)',
                  },
                }}
              >
                <Typography sx={{ color: '#fff', fontWeight: 600 }}>
                  Popularity
                </Typography>
              </Paper>
            </Box>
          </Box>
        </Box>

        {/* Compact header for mobile */}
        <Box
          sx={{
            display: isSmallScreen ? 'flex' : 'none',
            flexDirection: 'column',
            alignItems: 'center',
            mb: 2,
            gap: 1,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: '#fff',
              textAlign: 'center',
            }}
          >
            {characterDetails?.name || character.name || character.characters?.name}
          </Typography>
          <Typography variant="body2" sx={{ color: '#bbb', textAlign: 'center' }}>
            {characterDetails?.manga || character.manga || character.characters?.manga}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
            {(characterDetails?.role || character.role || character.characters?.role) && (
              <Chip
                label={characterDetails?.role || character.role || character.characters?.role}
                size="small"
                sx={{ background: '#444', color: '#fff' }}
              />
            )}
            {(characterDetails?.gender || character.gender || character.characters?.gender) && (
              <Chip
                label={characterDetails?.gender || character.gender || character.characters?.gender}
                size="small"
                sx={{ background: '#444', color: '#fff' }}
              />
            )}
          </Box>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 700,
              color: '#fff',
              mt: 1,
              mb: 1,
              textAlign: 'center',
            }}
          >
            Character Rankings
          </Typography>
        </Box>

        {/* Rankings Section - Full Width */}
        <Box sx={{ flex: 1, minHeight: 0 }}>
          {/* Loading State */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress sx={{ color: '#f49330' }} />
            </Box>
          ) : rankings.length === 0 ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <Typography sx={{ color: '#888' }}>
                No ranking data available for this character.
              </Typography>
            </Box>
          ) : (
            /* Horizontal Scrollable Rankings - Full Width */
            <Box sx={{ position: 'relative', height: '100%' }}>
              {/* Left Arrow */}
              <IconButton
                onClick={scrollLeft}
                sx={{
                  position: 'absolute',
                  left: -20,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 2,
                  background: 'rgba(35, 35, 35, 0.9)',
                  border: '2px solid #f49330',
                  color: '#f49330',
                  width: 40,
                  height: 40,
                  '&:hover': {
                    background: 'rgba(244, 147, 48, 0.1)',
                    borderColor: '#fff',
                    color: '#fff',
                  },
                }}
              >
                <ArrowBackIosIcon fontSize="small" />
              </IconButton>

              {/* Right Arrow */}
              <IconButton
                onClick={scrollRight}
                sx={{
                  position: 'absolute',
                  right: -20,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 2,
                  background: 'rgba(35, 35, 35, 0.9)',
                  border: '2px solid #f49330',
                  color: '#f49330',
                  width: 40,
                  height: 40,
                  '&:hover': {
                    background: 'rgba(244, 147, 48, 0.1)',
                    borderColor: '#fff',
                    color: '#fff',
                  },
                }}
              >
                <ArrowForwardIosIcon fontSize="small" />
              </IconButton>

              {/* Scrollable Container - Full Width */}
              <Box
                ref={scrollContainerRef}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseLeave}
                sx={{
                  display: 'flex',
                  gap: { xs: 1.5, sm: 3 }, // less gap on mobile
                  overflowX: 'auto',
                  overflowY: 'hidden',
                  px: { xs: 1, sm: 3 },
                  py: { xs: 1, sm: 2 },
                  cursor: isDragging ? 'grabbing' : 'grab',
                  height: '100%',
                  alignItems: 'center',
                  width: '100%',
                  userSelect: 'none',
                  '&:active': { cursor: 'grabbing' },
                  // Custom scrollbar
                  '&::-webkit-scrollbar': {
                    height: 8,
                  },
                  '&::-webkit-scrollbar-track': {
                    background: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: 4,
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: 'rgba(244, 147, 48, 0.6)',
                    borderRadius: 4,
                    '&:hover': {
                      background: 'rgba(244, 147, 48, 0.8)',
                    },
                  },
                }}
              >
                {getFilteredRankings().map((ranking, index) => (
                  <Paper
                    key={index}
                    sx={{
                      background: 'rgba(46, 46, 46, 0.8)',
                      border: '2px solid #555',
                      borderRadius: 3,
                      p: { xs: 1.5, sm: 3 },
                      textAlign: 'center',
                      minWidth: { xs: 180, sm: 280 },
                      maxWidth: { xs: 180, sm: 280 },
                      height: { xs: 300, sm: 450 },
                      flexShrink: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        borderColor: getTagTitleColor(ranking.type),
                        boxShadow: `0 8px 25px ${getTagTitleColor(ranking.type)}20`,
                        transform: 'translateY(-2px)',
                      },
                    }}
                  >
                    {/* Tag Name */}
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        mb: { xs: 0.5, sm: 1 },
                        color: getTagTitleColor(ranking.type),
                        fontSize: { xs: '1.1rem', sm: '1.25rem' },
                      }}
                    >
                      {ranking.tag === 'Female' ? 'Waifu' : ranking.tag}
                    </Typography>

                    {/* Tag Type */}
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#bbb',
                        mb: { xs: 1, sm: 2 },
                        fontSize: { xs: 12, sm: 14 },
                      }}
                    >
                      {ranking.type === 'power_level' ? 'Power' : ranking.type === 'popularity' ? 'Popularity' : ranking.type}
                    </Typography>

                    {/* Rank */}
                    <Typography
                      variant="h2"
                      sx={{
                        fontWeight: 900,
                        color: '#fff',
                        mb: { xs: 1, sm: 2 },
                        fontSize: { xs: '2.2rem', sm: '3.5rem' },
                        display: 'flex',
                        alignItems: 'baseline',
                        justifyContent: 'center',
                        gap: 1,
                      }}
                    >
                      {ranking.rank}
                      <Typography
                        component="span"
                        sx={{
                          fontSize: { xs: '0.5em', sm: '0.3em' },
                          fontWeight: 400,
                          color: '#bbb',
                          verticalAlign: 'top',
                        }}
                      >
                        {getRankSuffix(ranking.rank)}
                      </Typography>
                      <Typography
                        component="span"
                        sx={{
                          fontSize: { xs: '0.4em', sm: '0.25em' },
                          fontWeight: 400,
                          color: '#888',
                          ml: 0.5,
                        }}
                      >
                        /{ranking.totalCharacters}
                      </Typography>
                    </Typography>

                    {/* Score */}
                    <Typography
                      variant="h6"
                      sx={{
                        color: getTagTitleColor(ranking.type),
                        mb: { xs: 1, sm: 2 },
                        fontWeight: 600,
                        fontSize: { xs: '1.1rem', sm: '1.5rem' },
                      }}
                    >
                      Score: {ranking.score}
                    </Typography>

                    {/* Win/Loss Record */}
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#bbb',
                        mb: 0.5,
                        fontSize: { xs: 13, sm: 16 },
                      }}
                    >
                      {ranking.wins}W - {ranking.losses}L
                    </Typography>

                    {/* Winrate */}
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#bbb',
                        mb: 0.5,
                        fontSize: { xs: 13, sm: 16 },
                      }}
                    >
                      Win Rate: {formatWinrate(ranking.winrate)}
                    </Typography>

                    {/* Total Battles */}
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#888',
                        fontSize: { xs: 11, sm: 14 },
                      }}
                    >
                      {ranking.total_battles} battles
                    </Typography>
                  </Paper>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </Paper>
    </Modal>
  );
}